import React, { useState, useRef, useEffect } from "react";
import { Checkbox } from "@workspace/web-ui/components/checkbox";

interface Option {
  value: string;
  label: string;
  description?: string;
}

interface CustomSelectProps {
  options: Option[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);


  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        listRef.current &&
        !listRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Keyboard navigation (optional, basic)
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const selectedOption = options.find((opt) => opt.value === value);


  return (
    <div className={`relative ${className}`} tabIndex={-1}>
      <button
        type="button"
        ref={buttonRef}
        className={`w-full flex justify-between items-center border rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectedOption ? '' : 'text-gray-400'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <ul
          ref={listRef}
          className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto py-1"
          role="listbox"
        >
          {options.map((option) => (
            <li
              key={option.value}
              role="option"
              aria-selected={value === option.value}
              tabIndex={0}
              className={`flex items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 focus:bg-blue-100 ${value === option.value ? "bg-blue-100 font-semibold" : ""
                }`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onChange(option.value);
                  setOpen(false);
                }
              }}
            >
              <span>
                <span className="font-medium text-sm">{option.label}</span>
                {option.description && (
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {option.description}
                  </span>
                )}
              </span>
              <span
                className={`inline-flex h-4 w-4 rounded-full items-center justify-center border border-primary bg-white ${value === option.value ? "bg-blue-600 border-blue-600" : ""}`}
                aria-checked={value === option.value}
              >
                <Checkbox checked={value === option.value} tabIndex={-1} className="pointer-events-none h-4 w-4 border-none bg-transparent rounded-full" />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}; 